import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, MetaRow } from '@/ui';
import { useAsyncRead } from '@/hooks/useAsyncRead';
import type { Cadence, MixFeed, Payloads, Resource, Source } from './contracts';
import { resourceHref } from './contracts';
import { networks } from './service';
import { providerUrl } from './providerUrl';
import { CadenceField } from './ResourceEditor';
import { Card, Check, Field, Form, Quote, Row, Small, Stack } from './styles';

export type Act = (action: string, values?: Record<string, unknown>) => Promise<boolean>;
export function SourceCard({ source }: { source: Source }) {
  if (!source.available) return <Small>Source unavailable. It may be private or removed.</Small>;
  // Never trust a server-provided URL as executable or cross-origin navigation.
  const href = `/post/${encodeURIComponent(source.postId)}${source.commentId ? `?comment=${encodeURIComponent(source.commentId)}` : ''}`;
  return <Link to={href}>{source.title || 'Open source'}</Link>;
}
export function MixReader({ resource, owner }: { resource: Resource; owner: string }) {
  const [mode, setMode] = useState((resource.data as Payloads['mixes']).mode);
  const [page, setPage] = useState<{ cursor?: string; snapshot?: string }>({});
  const load = useCallback(
    () => networks.feed(resource.slug, mode, page.cursor, page.snapshot),
    [resource.slug, mode, page],
  );
  const result = useAsyncRead<MixFeed | null>(load, owner, null);
  return (
    <Stack>
      <Row>
        {(['recent', 'relevant', 'catch_up'] as const).map((m) => (
          <Button
            key={m}
            aria-pressed={mode === m}
            variant={mode === m ? 'secondary' : 'ghost'}
            onClick={() => {
              setMode(m);
              setPage({});
            }}
          >
            {m === 'catch_up' ? 'Catch Up' : m === 'recent' ? 'Recent' : 'Relevant'}
          </Button>
        ))}
      </Row>
      {mode === 'catch_up' && (
        <Small>
          A finite snapshot of new posts since your last completed catch-up. New arrivals wait for
          your next visit.
        </Small>
      )}
      {result.loading ? (
        <p role="status">Loading Mix…</p>
      ) : result.error ? (
        <>
          <p role="alert">Could not load this Mix.</p>
          <Button onClick={result.retry}>Retry</Button>
        </>
      ) : (
        <>
          {result.data?.items.map((p) => (
            <Card key={p.id}>
              <Link to={`/profile/${encodeURIComponent(p.author.handle)}`}>@{p.author.handle}</Link>
              <p>{p.body}</p>
              <Link to={`/post/${encodeURIComponent(p.id)}`}>Open discussion</Link>
            </Card>
          ))}
          {!result.data?.items.length && <p>No visible posts match this Mix yet.</p>}
          {result.data?.next ? (
            <Button
              onClick={() =>
                setPage({ cursor: result.data!.next!, snapshot: result.data!.snapshot })
              }
            >
              Next page
            </Button>
          ) : (
            mode === 'catch_up' &&
            result.data?.complete && (
              <CatchUpDone resource={resource} snapshot={result.data.snapshot} />
            )
          )}
        </>
      )}
    </Stack>
  );
}
function CatchUpDone({ resource, snapshot }: { resource: Resource; snapshot: string }) {
  const [state, setState] = useState<'ready' | 'saving' | 'done' | 'error'>('ready');
  return (
    <Card>
      <h3>You're caught up</h3>
      <p>That's the end of this snapshot. You can leave knowing where you stopped.</p>
      {state === 'done' ? (
        <p role="status">Reading checkpoint saved.</p>
      ) : (
        <>
          <Button
            disabled={state === 'saving'}
            onClick={() => {
              setState('saving');
              void networks.action(resource, 'complete_catch_up', { snapshot }).then(
                () => setState('done'),
                () => setState('error'),
              );
            }}
          >
            Save reading checkpoint
          </Button>
          {state === 'error' && <p role="alert">Checkpoint was not saved. Try again.</p>}
        </>
      )}
    </Card>
  );
}
export function PackReader({
  resource,
  act,
  busy,
  signedIn,
}: {
  resource: Resource;
  act: Act;
  busy: boolean;
  signedIn: boolean;
}) {
  const choices = (resource.data as Payloads['starter-packs']).choices;
  const [selected, setSelected] = useState<string[]>([]);
  const applicable = choices
    .filter((c) => selected.includes(c.id) && c.state === 'available')
    .map((c) => c.id);
  return (
    <Stack>
      <Small>
        Select only what you want. Private accounts require follow approval; a pack never bypasses
        it.
      </Small>
      {choices.map((c) => (
        <Check key={c.id}>
          <input
            type="checkbox"
            checked={selected.includes(c.id)}
            disabled={c.state !== 'available' || busy || !signedIn}
            onChange={(e) =>
              setSelected(
                e.target.checked ? [...selected, c.id] : selected.filter((id) => id !== c.id),
              )
            }
          />
          <MetaRow>
            {c.label}
            {c.kind}
            {c.state}
          </MetaRow>
        </Check>
      ))}
      <Button
        disabled={busy || !signedIn || !applicable.length}
        onClick={() =>
          void act('apply', { choices: applicable }).then((ok) => {
            if (ok) setSelected([]);
          })
        }
      >
        Apply {applicable.length} selected choices
      </Button>
    </Stack>
  );
}
export function EventReader({
  resource,
  act,
  busy,
  signedIn,
}: {
  resource: Resource;
  act: Act;
  busy: boolean;
  signedIn: boolean;
}) {
  const d = resource.data as Payloads['events'];
  const [question, setQuestion] = useState('');
  const [answerPost, setAnswerPost] = useState('');
  const [answerComment, setAnswerComment] = useState('');
  const owner = resource.role === 'owner';
  const closed = d.state === 'ended' || d.state === 'cancelled';
  return (
    <Stack>
      <p>
        <MetaRow>
          <strong>{d.state}</strong>
          {d.kind === 'text_ama' ? 'Text AMA' : 'External live session'}
        </MetaRow>
      </p>
      <p>
        <time dateTime={d.startsAt}>{new Date(d.startsAt).toLocaleString()}</time> –{' '}
        <time dateTime={d.endsAt}>{new Date(d.endsAt).toLocaleString()}</time>
      </p>
      <Small>
        Displayed in {Intl.DateTimeFormat().resolvedOptions().timeZone}. Event organizer's zone:{' '}
        {d.timezone}.
      </Small>
      {d.community && (
        <Link to={`/communities/${encodeURIComponent(d.community)}`}>Open community</Link>
      )}
      <Row>
        <Button
          disabled={busy || !signedIn || closed}
          onClick={() => void act('rsvp', { enabled: !resource.rsvp })}
        >
          {resource.rsvp ? 'Cancel RSVP' : 'RSVP'}
        </Button>
        <Button
          disabled={busy || !signedIn || closed}
          onClick={() => void act('reminder', { enabled: !resource.reminder })}
        >
          {resource.reminder ? 'Remove reminder' : 'Remind me'}
        </Button>
      </Row>
      {d.providerUrl && <ProviderLink url={d.providerUrl} label="Open live provider" />}
      {d.replayUrl && <ProviderLink url={d.replayUrl} label="Watch replay" />}
      {owner && !closed && (
        <Row>
          {d.state === 'scheduled' && (
            <Button disabled={busy} onClick={() => void act('event_state', { state: 'live' })}>
              Open room
            </Button>
          )}
          <Button disabled={busy} onClick={() => void act('event_state', { state: 'ended' })}>
            End room
          </Button>
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => void act('event_state', { state: 'cancelled' })}
          >
            Cancel event
          </Button>
        </Row>
      )}
      <Small>Organizers manage this event, not Council decisions or platform moderation.</Small>
      {d.recap && (
        <Card>
          <h3>Recap</h3>
          <p>{d.recap}</p>
        </Card>
      )}
      <h3>Questions</h3>
      {signedIn && !closed && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void act('question', { text: question }).then((ok) => {
              if (ok) setQuestion('');
            });
          }}
        >
          <Field>
            Your question
            <textarea
              required
              maxLength={2000}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
            />
          </Field>
          <Button type="submit" disabled={busy || !question.trim()}>
            Submit question
          </Button>
        </form>
      )}
      {owner && (
        <Card>
          <Small>
            To link an answer, enter its exact post and optional comment, then select a question
            below.
          </Small>
          <Field>
            Answer post ID
            <input value={answerPost} onChange={(e) => setAnswerPost(e.target.value)} />
          </Field>
          <Field>
            Answer comment ID
            <input value={answerComment} onChange={(e) => setAnswerComment(e.target.value)} />
          </Field>
        </Card>
      )}
      {d.questions.map((q) => (
        <Card key={q.id}>
          <Small>@{q.author.handle}</Small>
          <p>{q.text}</p>
          {q.answer ? <SourceCard source={q.answer} /> : <Small>Awaiting an answer</Small>}
          <Row>
            <Button
              variant="ghost"
              disabled={busy || !signedIn || closed}
              onClick={() => void act('question_vote', { questionId: q.id, enabled: !q.voted })}
            >
              <MetaRow>
                {q.voted ? 'Remove vote' : 'Useful question'}
                {String(q.votes)}
              </MetaRow>
            </Button>
            {owner && (
              <Button
                disabled={busy || !answerPost.trim()}
                onClick={() =>
                  void act('answer', {
                    questionId: q.id,
                    postId: answerPost.trim(),
                    commentId: answerComment.trim(),
                  })
                }
              >
                Link this answer
              </Button>
            )}
          </Row>
        </Card>
      ))}
    </Stack>
  );
}
export function ProviderLink({ url, label }: { url: string; label: string }) {
  const safe = providerUrl(url);
  return safe ? (
    <a href={safe.url} target="_blank" rel="noopener noreferrer">
      {label} (opens provider)
    </a>
  ) : (
    <Small>Provider link unavailable.</Small>
  );
}
function Video({
  item,
  act,
  busy,
  editor,
  signedIn,
}: {
  item: Payloads['media/playlists']['items'][number];
  act: Act;
  busy: boolean;
  editor: boolean;
  signedIn: boolean;
}) {
  const [play, setPlay] = useState(false);
  const [seconds, setSeconds] = useState('0');
  const [title, setTitle] = useState('');
  const [postId, setPostId] = useState('');
  const [comment, setComment] = useState('');
  const safe = providerUrl(item.url);
  return (
    <Card>
      <h3>{item.title}</h3>
      {!item.available || !safe ? (
        <Small>This video is unavailable. Its place in the playlist is preserved.</Small>
      ) : (
        <>
          <ProviderLink url={item.url} label="Watch video" />
          {safe.embed &&
            (play ? (
              <iframe
                title={item.title}
                src={safe.embed}
                loading="lazy"
                referrerPolicy="no-referrer"
                allow="fullscreen; picture-in-picture"
                style={{ width: '100%', aspectRatio: '16/9', border: 0 }}
              />
            ) : (
              <Button variant="secondary" onClick={() => setPlay(true)}>
                Load YouTube player
              </Button>
            ))}
          <Small>
            Playback is provided by {item.provider}. Availability and tracking are controlled by
            that provider.
          </Small>
        </>
      )}
      {item.chapters.map((c) => (
        <p key={c.seconds}>
          <MetaRow>
            {`${Math.floor(c.seconds / 60)}:${String(c.seconds % 60).padStart(2, '0')}`}
            {c.title}
          </MetaRow>
        </p>
      ))}
      <Field>
        Timestamp in seconds
        <input
          type="number"
          min={0}
          max={86400}
          value={seconds}
          onChange={(e) => setSeconds(e.target.value)}
        />
      </Field>
      {item.postId && (
        <Link to={`/post/${encodeURIComponent(item.postId)}`}>Open video discussion</Link>
      )}
      {item.comments?.map((c) => (
        <p key={`${c.source.postId}:${c.source.commentId}`}>
          <MetaRow>
            {`${Math.floor(c.seconds / 60)}:${String(c.seconds % 60).padStart(2, '0')}`}
            <SourceCard source={c.source} />
          </MetaRow>
        </p>
      ))}
      {item.postId && signedIn && (
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            void act('video_comment', {
              itemId: item.id,
              seconds: Number(seconds),
              text: comment,
            }).then((ok) => {
              if (ok) setComment('');
            });
          }}
        >
          <Field>
            Comment at this timestamp
            <textarea
              required
              value={comment}
              maxLength={2000}
              onChange={(e) => setComment(e.target.value)}
            />
          </Field>
          <Button type="submit" disabled={busy || !comment.trim()}>
            Post timestamp comment
          </Button>
        </Form>
      )}
      {editor && (
        <>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              void act('video_discussion', { itemId: item.id, postId }).then((ok) => {
                if (ok) setPostId('');
              });
            }}
          >
            <Field>
              Existing discussion post ID (owned by playlist creator)
              <input required value={postId} onChange={(e) => setPostId(e.target.value)} />
            </Field>
            <Button type="submit" disabled={busy}>
              Link discussion
            </Button>
          </Form>
          <Field>
            Chapter title
            <input maxLength={100} value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>
          <Button
            disabled={busy || !title.trim()}
            onClick={() =>
              void act('chapter', { itemId: item.id, seconds: Number(seconds), title }).then(
                (ok) => {
                  if (ok) setTitle('');
                },
              )
            }
          >
            Add chapter
          </Button>
        </>
      )}
    </Card>
  );
}
export function ResourceContent({
  resource: r,
  act,
  busy,
  userId,
}: {
  resource: Resource;
  act: Act;
  busy: boolean;
  userId: string;
}) {
  const [handle, setHandle] = useState('');
  const [memberRole, setMemberRole] = useState<'editor' | 'viewer'>('viewer');
  const [body, setBody] = useState('');
  const [cadence, setCadence] = useState<Cadence>(
    r.kind === 'channels' ? (r.data as Payloads['channels']).cadence : 'none',
  );
  const signedIn = userId !== 'guest';
  const owner = r.role === 'owner';
  const editor = owner || r.role === 'editor';
  if (r.kind === 'mixes')
    return (
      <>
        <Small>
          <MetaRow>
            {(r.data as Payloads['mixes']).rules.map((rule, i) => (
              <span key={i}>{`${rule.kind}: ${rule.value}`}</span>
            ))}
          </MetaRow>
        </Small>
        <Row>
          <Button
            disabled={busy || !signedIn}
            onClick={() => void act('subscribe', { enabled: !r.subscribed })}
          >
            {r.subscribed ? 'Unsubscribe' : 'Subscribe to Mix'}
          </Button>
          <Button
            disabled={busy || !signedIn}
            onClick={() => void act('pin', { enabled: !r.pinned })}
          >
            {r.pinned ? 'Unpin' : 'Pin to Home'}
          </Button>
        </Row>
        <MixReader resource={r} owner={userId} />
      </>
    );
  if (r.kind === 'starter-packs')
    return <PackReader resource={r} act={act} busy={busy} signedIn={signedIn} />;
  if (r.kind === 'events')
    return <EventReader resource={r} act={act} busy={busy} signedIn={signedIn} />;
  if (r.kind === 'collections') {
    const d = r.data as Payloads['collections'];
    return (
      <Stack>
        <Small>This shared collection is separate from your private bookmarks and notes.</Small>
        {d.items.map((i) => (
          <Card key={i.id}>
            <SourceCard source={i.source} />
            <p>{i.annotation}</p>
          </Card>
        ))}
        {owner && (
          <Card>
            <h3>Collection access</h3>
            <Field>
              Member handle
              <input value={handle} maxLength={80} onChange={(e) => setHandle(e.target.value)} />
            </Field>
            <Field>
              Role
              <select
                value={memberRole}
                onChange={(e) => setMemberRole(e.target.value as typeof memberRole)}
              >
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
              </select>
            </Field>
            <Button
              disabled={busy || !handle.trim()}
              onClick={() =>
                void act('member', { handle, role: memberRole }).then((ok) => {
                  if (ok) setHandle('');
                })
              }
            >
              Grant access
            </Button>
            {d.members.map((m) => (
              <Row key={m.person.id}>
                <MetaRow>
                  {`@${m.person.handle}`}
                  {m.role}
                </MetaRow>
                <Button
                  disabled={busy}
                  variant="ghost"
                  onClick={() => void act('member', { handle: m.person.handle, role: 'remove' })}
                >
                  Remove access
                </Button>
              </Row>
            ))}
          </Card>
        )}
        {editor && (
          <Button disabled={busy} onClick={() => void act('create_guide')}>
            Create a guide draft from sources
          </Button>
        )}
      </Stack>
    );
  }
  if (r.kind === 'guides') {
    const d = r.data as Payloads['guides'];
    return (
      <Stack>
        <Small>
          <MetaRow>
            {d.state}
            {`Revision ${r.revision}`}
          </MetaRow>
        </Small>
        {d.blocks.map((b) => (
          <Card key={b.id}>
            {b.kind === 'quote' ? (
              <>
                <Quote>
                  {b.consent === 'accepted' || editor || b.contributor?.id === userId
                    ? b.text
                    : 'Quotation awaiting contributor consent or withdrawn.'}
                </Quote>
                <Small>
                  <MetaRow>
                    {`@${b.contributor?.handle ?? ''}`}
                    {b.consent}
                  </MetaRow>
                </Small>
                {b.contributor?.id === userId && (
                  <Row>
                    <Button
                      disabled={busy || b.consent === 'accepted'}
                      onClick={() => void act('consent', { blockId: b.id, accepted: true })}
                    >
                      Allow quotation
                    </Button>
                    <Button
                      disabled={busy || b.consent === 'withdrawn'}
                      onClick={() => void act('consent', { blockId: b.id, accepted: false })}
                    >
                      Withdraw quotation
                    </Button>
                  </Row>
                )}
              </>
            ) : (
              <p>{b.text}</p>
            )}
            {b.source && <SourceCard source={b.source} />}
          </Card>
        ))}
        {owner && (
          <Button disabled={busy} onClick={() => void act('publish')}>
            {d.state === 'published' ? 'Publish updated revision' : 'Publish guide'}
          </Button>
        )}
      </Stack>
    );
  }
  if (r.kind === 'post-collaborations') {
    const d = r.data as Payloads['post-collaborations'];
    const mine = d.collaborators.find((c) => c.person.id === userId);
    return (
      <Stack>
        <Card>
          <p>{d.body}</p>
          <Small>
            <MetaRow>
              {d.state}
              {`Draft revision ${r.revision}`}
            </MetaRow>
          </Small>
          {d.postId && (
            <Link to={`/post/${encodeURIComponent(d.postId)}`}>Open published post</Link>
          )}
        </Card>
        {d.collaborators.map((c) => (
          <Small key={c.person.id}>
            <MetaRow>
              {`@${c.person.handle}`}
              {c.state}
            </MetaRow>
          </Small>
        ))}
        {owner && d.state === 'draft' && (
          <>
            <Field>
              Invite co-author by handle
              <input value={handle} maxLength={80} onChange={(e) => setHandle(e.target.value)} />
            </Field>
            <Button
              disabled={busy || !handle.trim()}
              onClick={() =>
                void act('invite', { handle }).then((ok) => {
                  if (ok) setHandle('');
                })
              }
            >
              Invite co-author
            </Button>
            <Button disabled={busy} onClick={() => void act('publish')}>
              Publish with accepted co-authors
            </Button>
          </>
        )}
        {mine && (
          <Row>
            {mine.state === 'pending' && (
              <>
                <Button disabled={busy} onClick={() => void act('respond', { state: 'accepted' })}>
                  Accept invitation
                </Button>
                <Button disabled={busy} onClick={() => void act('respond', { state: 'declined' })}>
                  Decline
                </Button>
              </>
            )}
            {mine.state === 'accepted' && (
              <Button disabled={busy} onClick={() => void act('respond', { state: 'withdrawn' })}>
                Withdraw attribution
              </Button>
            )}
          </Row>
        )}
        {editor && d.state === 'published' && (
          <>
            <Field>
              Propose a revised post
              <textarea maxLength={10000} value={body} onChange={(e) => setBody(e.target.value)} />
            </Field>
            <Button
              disabled={busy || !body.trim()}
              onClick={() =>
                void act('propose', { body }).then((ok) => {
                  if (ok) setBody('');
                })
              }
            >
              Submit revision proposal
            </Button>
          </>
        )}
        {d.proposals.map((p) => (
          <Card key={p.id}>
            <Small>
              <MetaRow>
                {`@${p.author.handle}`}
                {p.state}
              </MetaRow>
            </Small>
            <p>{p.body}</p>
            {owner && p.state === 'pending' && (
              <Row>
                <Button
                  disabled={busy}
                  onClick={() => void act('resolve_proposal', { proposalId: p.id, accepted: true })}
                >
                  Accept revision
                </Button>
                <Button
                  disabled={busy}
                  onClick={() =>
                    void act('resolve_proposal', { proposalId: p.id, accepted: false })
                  }
                >
                  Decline revision
                </Button>
              </Row>
            )}
          </Card>
        ))}
      </Stack>
    );
  }
  if (r.kind === 'channels') {
    const d = r.data as Payloads['channels'];
    return (
      <Stack>
        <Small>
          Following the creator and subscribing to this channel are independent. A subscription
          never grants access to private posts.
        </Small>
        <CadenceField value={cadence} onChange={setCadence} />
        <Button
          disabled={busy || !signedIn}
          onClick={() => void act('subscribe', { enabled: true, cadence })}
        >
          Save subscription frequency
        </Button>
        {r.subscribed && (
          <Button
            disabled={busy}
            variant="ghost"
            onClick={() => void act('subscribe', { enabled: false })}
          >
            Unsubscribe
          </Button>
        )}
        {owner && (
          <>
            <Field>
              Published post ID
              <input value={body} onChange={(e) => setBody(e.target.value)} />
            </Field>
            <Button
              disabled={busy || !body.trim()}
              onClick={() =>
                void act('channel_post', { postId: body.trim() }).then((ok) => {
                  if (ok) setBody('');
                })
              }
            >
              Add own post to channel
            </Button>
          </>
        )}
        {d.postIds.map((id) => (
          <Link key={id} to={`/post/${encodeURIComponent(id)}`}>
            Open channel post
          </Link>
        ))}
      </Stack>
    );
  }
  if (r.kind === 'media/playlists')
    return (
      <Stack>
        {(r.data as Payloads['media/playlists']).items.map((item) => (
          <Video
            key={item.id}
            item={item}
            act={act}
            busy={busy}
            editor={editor}
            signedIn={signedIn}
          />
        ))}
      </Stack>
    );
  if (r.kind === 'saved-searches') {
    const d = r.data as Payloads['saved-searches'];
    const q = new URLSearchParams(Object.entries(d.filters).filter(([, value]) => !!value));
    q.set('q', d.filters.query);
    q.delete('query');
    return (
      <Stack>
        <Small>Delivery: {d.cadence}. Only content you can access will appear.</Small>
        <Link to={`/explore?${q}`}>Run this search</Link>
        <Link to="/notification-delivery">Manage notification budget and quiet hours</Link>
      </Stack>
    );
  }
  return <Link to={resourceHref(r)}>Open resource</Link>;
}
